import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useFrontAuthStore } from '../store/authStore';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.gray[50]};
  gap: ${props => props.theme.spacing.lg};
`;

const Spinner = styled.div`
  width: 3rem;
  height: 3rem;
  border: 4px solid ${props => props.theme.colors.gray[200]};
  border-top: 4px solid ${props => props.theme.colors.toss.blue};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.p`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.gray[600]};
`;

interface FrontProtectedRouteProps {
  children: React.ReactNode;
  redirectIfAuthenticated?: boolean;
}

const FrontProtectedRoute: React.FC<FrontProtectedRouteProps> = ({ 
  children, 
  redirectIfAuthenticated = false 
}) => {
  const { user, isAuthenticated, isLoading } = useFrontAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
        <LoadingText>인증 확인 중...</LoadingText>
      </LoadingContainer>
    );
  }

  if (redirectIfAuthenticated && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!redirectIfAuthenticated && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default FrontProtectedRoute;
